import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  ArrowLeft, Car, User, Camera, Loader2, Plus, X, CheckCircle2, Search, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PhotosCorners, { PhotoCornerSlot } from "@/components/PhotosCorners";

const FUEL_LEVELS = ["vide", "1/4", "1/2", "3/4", "plein"];

function nowLocalIso() {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzOffset).toISOString().slice(0, 16);
}

function ClientPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q || value) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data.clients || []);
      } catch (e) { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, value]);

  if (value) {
    return (
      <div className="flex items-center gap-2 p-2.5 border border-gray-200 rounded-md bg-gray-50">
        <User className="w-4 h-4 text-[#52a675]" />
        <div className="flex-1 text-sm">
          <span className="font-semibold">{value.nom}</span>
          <span className="text-gray-500 ml-2">{value.tel_principal}</span>
        </div>
        <button onClick={() => onChange(null)} className="text-gray-400 hover:text-red-600" data-testid="pret-clear-client" type="button">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Rechercher un client (optionnel)…" className="pl-10" data-testid="pret-client-search" />
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {results.map((c) => {
            const nom = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => { onChange({ id: c.id, nom, tel_principal: c.tel_principal, prenom: c.prenom, nom_seul: c.nom, email: c.email, adresse: c.adresse, npa: c.npa, localite: c.localite }); setQ(""); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-[#f0f9f4] text-sm border-b border-gray-50 last:border-0"
                data-testid={`pret-pick-client-${c.id}`}
              >
                <div className="font-semibold">{nom}</div>
                <div className="text-xs text-gray-500">{c.tel_principal} · {c.localite}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function PretCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  // Réservation préremplie (depuis /remplacement → bouton "Démarrer le prêt")
  const prefillReservation = location.state?.reservation || null;
  const [flotte, setFlotte] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [flotteId, setFlotteId] = useState(prefillReservation?.flotte_id || "");
  const [client, setClient] = useState(null);
  const [conducteur, setConducteur] = useState({
    nom: "", prenom: "", tel: "", email: "", adresse: "",
    permis_numero: "", permis_categorie: "B",
    permis_date_delivrance: "",
    permis_photo_recto: null, permis_photo_verso: null,
    piece_identite_type: "ci", piece_identite_numero: "",
  });
  const [depose, setDepose] = useState({
    date_heure: nowLocalIso(),
    km_depart: "",
    niveau_carburant: "plein",
    etat_initial: "",
    date_retour_prevu: prefillReservation?.date_fin ? prefillReservation.date_fin.slice(0, 16) : "",
    tarif_jour: prefillReservation?.tarif_jour ?? 0,
    courtoisie: (prefillReservation?.tarif_jour ?? -1) === 0,
    caution_montant: 0,
    caution_mode: "aucune",
  });
  const [photos, setPhotos] = useState({});
  // rdv_id à transmettre au backend pour conversion auto de la réservation
  const [rdvId] = useState(prefillReservation?.rdv_id || null);

  useEffect(() => {
    api.get("/flotte?actif_only=true").then((r) => {
      // Si on a une réservation pré-remplie, le véhicule réservé peut être en_pret ou disponible
      // → on l'inclut systématiquement pour qu'il soit sélectionnable.
      const items = r.data.filter((f) => f.statut === "disponible" || f.id === prefillReservation?.flotte_id);
      setFlotte(items);
    }).catch(() => {});
  }, [prefillReservation?.flotte_id]);

  // Si réservation a un client_id, le charger automatiquement
  useEffect(() => {
    if (prefillReservation?.client_id && !client) {
      api.get(`/clients/${prefillReservation.client_id}`)
        .then(({ data }) => {
          const nom = data.raison_sociale || `${data.prenom || ""} ${data.nom || ""}`.trim();
          setClient({
            id: data.id, nom, tel_principal: data.tel_principal,
            prenom: data.prenom, nom_seul: data.nom, email: data.email,
            adresse: data.adresse, npa: data.npa, localite: data.localite,
          });
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillReservation?.client_id]);

  // Préremplir conducteur quand un client est choisi
  useEffect(() => {
    if (client) {
      setConducteur((c) => ({
        ...c,
        nom: c.nom || client.nom_seul || "",
        prenom: c.prenom || client.prenom || "",
        tel: c.tel || client.tel_principal || "",
        email: c.email || client.email || "",
        adresse: c.adresse || [client.adresse, client.npa, client.localite].filter(Boolean).join(", "),
      }));
    }
  }, [client]);

  // Tarif par défaut depuis flotte
  useEffect(() => {
    if (!flotteId) return;
    const f = flotte.find((x) => x.id === flotteId);
    if (f && !depose.tarif_jour) {
      setDepose((d) => ({ ...d, tarif_jour: f.tarif_jour || 0 }));
    }
  }, [flotteId]); // eslint-disable-line

  const submit = async () => {
    // Validations
    const errs = [];
    if (!flotteId) errs.push("Véhicule de la flotte");
    if (!conducteur.nom || !conducteur.prenom) errs.push("Nom et prénom du conducteur");
    if (!conducteur.tel) errs.push("Téléphone du conducteur");
    if (!conducteur.permis_numero) errs.push("N° de permis");
    if (!depose.date_retour_prevu) errs.push("Date retour prévu");
    if (errs.length) {
      toast.error("Champs requis manquants : " + errs.join(", "));
      return;
    }
    setSubmitting(true);
    try {
      const photosArr = [];
      ["av_g", "av_d", "ar_g", "ar_d"].forEach((k) => { if (photos[k]) photosArr.push(photos[k]); });

      const payload = {
        flotte_id: flotteId,
        client_id: client?.id || null,
        rdv_id: rdvId || null,
        depose: {
          date_heure: new Date(depose.date_heure).toISOString(),
          km_depart: depose.km_depart === "" ? null : parseInt(depose.km_depart, 10),
          niveau_carburant: depose.niveau_carburant,
          etat_initial: depose.etat_initial || null,
          photos: photosArr,
          conducteur: {
            ...conducteur,
            permis_date_delivrance: conducteur.permis_date_delivrance || null,
          },
          date_retour_prevu: new Date(depose.date_retour_prevu).toISOString(),
          tarif_jour: depose.courtoisie ? 0 : parseFloat(depose.tarif_jour || 0),
          courtoisie: depose.courtoisie,
          caution_montant: parseFloat(depose.caution_montant || 0),
          caution_mode: depose.caution_mode,
        },
      };
      const { data } = await api.post("/prets", payload);
      toast.success(`Prêt ${data.numero} créé`);
      navigate(`/remplacement/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto pb-24" data-testid="pret-create-page">
      <button onClick={() => navigate("/remplacement")} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <h1 className="text-2xl md:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-6">
        <Car className="w-7 h-7 text-[#52a675]" /> Nouveau prêt véhicule
      </h1>

      {prefillReservation && (
        <Card className="p-3 mb-4 bg-blue-50 border-blue-200" data-testid="pret-prefill-banner">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-blue-900">
                Démarrage du prêt depuis une réservation
              </div>
              <div className="text-xs text-blue-800 mt-0.5">
                {prefillReservation.flotte_label} · {prefillReservation.client_nom || "client à compléter"} ·
                période {prefillReservation.date_debut?.slice(0, 10)} → {prefillReservation.date_fin?.slice(0, 10)}
                {prefillReservation.tarif_jour > 0 && ` · ${prefillReservation.tarif_jour} CHF/j`}
                {prefillReservation.tarif_jour === 0 && " · offert"}
              </div>
              <div className="text-[11px] text-blue-700 mt-1 italic">
                Complétez la fiche conducteur (permis, signatures, photos) puis validez. La réservation sera automatiquement convertie en prêt actif.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* 1. Véhicule */}
      <Card className="p-5 border-gray-200 mb-4" data-testid="pret-step-vehicule">
        <div className="text-xs uppercase tracking-wider text-[#52a675] font-bold mb-3">1 · VÉHICULE DE LA FLOTTE</div>
        {flotte.length === 0 ? (
          <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-3">
            Aucun véhicule disponible dans la flotte. <button className="underline font-semibold" onClick={() => navigate("/remplacement?tab=flotte")}>Ajouter un véhicule</button>
          </div>
        ) : (
          <Select value={flotteId} onValueChange={setFlotteId}>
            <SelectTrigger data-testid="pret-flotte-select"><SelectValue placeholder="Sélectionner un véhicule disponible…" /></SelectTrigger>
            <SelectContent>
              {flotte.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.plaque} · {f.marque} {f.modele}{f.annee ? ` (${f.annee})` : ""} — {(f.tarif_jour || 0).toFixed(0)} CHF/j
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Card>

      {/* 2. Client / Conducteur */}
      <Card className="p-5 border-gray-200 mb-4" data-testid="pret-step-conducteur">
        <div className="text-xs uppercase tracking-wider text-[#52a675] font-bold mb-3">2 · CONDUCTEUR</div>
        <Label className="text-xs">Lier à un client existant (optionnel)</Label>
        <ClientPicker value={client} onChange={setClient} />

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <Label>Prénom *</Label>
            <Input value={conducteur.prenom} onChange={(e) => setConducteur({ ...conducteur, prenom: e.target.value })} data-testid="pret-prenom" />
          </div>
          <div>
            <Label>Nom *</Label>
            <Input value={conducteur.nom} onChange={(e) => setConducteur({ ...conducteur, nom: e.target.value })} data-testid="pret-nom" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label>Téléphone *</Label>
            <Input value={conducteur.tel} onChange={(e) => setConducteur({ ...conducteur, tel: e.target.value })} placeholder="+41 79…" data-testid="pret-tel" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={conducteur.email} onChange={(e) => setConducteur({ ...conducteur, email: e.target.value })} data-testid="pret-email" />
          </div>
        </div>
        <div className="mt-3">
          <Label>Adresse</Label>
          <Input value={conducteur.adresse} onChange={(e) => setConducteur({ ...conducteur, adresse: e.target.value })} placeholder="Rue, NPA, localité" data-testid="pret-adresse" />
        </div>
      </Card>

      {/* 3. Permis */}
      <Card className="p-5 border-gray-200 mb-4" data-testid="pret-step-permis">
        <div className="text-xs uppercase tracking-wider text-[#52a675] font-bold mb-3">3 · PERMIS DE CONDUIRE</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>N° permis *</Label>
            <Input value={conducteur.permis_numero} onChange={(e) => setConducteur({ ...conducteur, permis_numero: e.target.value.toUpperCase() })} className="font-mono" data-testid="pret-permis-num" />
          </div>
          <div>
            <Label>Catégorie</Label>
            <Select value={conducteur.permis_categorie} onValueChange={(v) => setConducteur({ ...conducteur, permis_categorie: v })}>
              <SelectTrigger data-testid="pret-permis-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["B", "B1", "BE", "A", "A1", "C", "CE", "D"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Délivrance</Label>
            <Input type="date" value={conducteur.permis_date_delivrance} onChange={(e) => setConducteur({ ...conducteur, permis_date_delivrance: e.target.value })} data-testid="pret-permis-deliv" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <Label className="text-xs uppercase text-gray-500 font-semibold">Scan permis recto</Label>
            <div className="mt-1">
              <PhotoCornerSlot label="Permis - recto" value={conducteur.permis_photo_recto} onChange={(v) => setConducteur({ ...conducteur, permis_photo_recto: v })} testid="pret-permis-recto" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-gray-500 font-semibold">Scan permis verso</Label>
            <div className="mt-1">
              <PhotoCornerSlot label="Permis - verso" value={conducteur.permis_photo_verso} onChange={(v) => setConducteur({ ...conducteur, permis_photo_verso: v })} testid="pret-permis-verso" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div>
            <Label>Pièce d'identité</Label>
            <Select value={conducteur.piece_identite_type} onValueChange={(v) => setConducteur({ ...conducteur, piece_identite_type: v })}>
              <SelectTrigger data-testid="pret-id-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ci">Carte d'identité</SelectItem>
                <SelectItem value="passeport">Passeport</SelectItem>
                <SelectItem value="permis_sejour">Permis de séjour</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>N° pièce d'identité</Label>
            <Input value={conducteur.piece_identite_numero} onChange={(e) => setConducteur({ ...conducteur, piece_identite_numero: e.target.value.toUpperCase() })} className="font-mono" data-testid="pret-id-num" />
          </div>
        </div>
      </Card>

      {/* 4. État dépose */}
      <Card className="p-5 border-gray-200 mb-4" data-testid="pret-step-depose">
        <div className="text-xs uppercase tracking-wider text-[#52a675] font-bold mb-3">4 · ÉTAT À LA DÉPOSE</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Date / heure dépose *</Label>
            <Input type="datetime-local" value={depose.date_heure} onChange={(e) => setDepose({ ...depose, date_heure: e.target.value })} data-testid="pret-date-depose" />
          </div>
          <div>
            <Label>Retour prévu *</Label>
            <Input type="datetime-local" value={depose.date_retour_prevu} onChange={(e) => setDepose({ ...depose, date_retour_prevu: e.target.value })} data-testid="pret-date-retour-prevu" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label>Km au départ</Label>
            <Input type="number" value={depose.km_depart} onChange={(e) => setDepose({ ...depose, km_depart: e.target.value })} placeholder="Optionnel" data-testid="pret-km-depart" />
          </div>
          <div>
            <Label>Niveau carburant</Label>
            <Select value={depose.niveau_carburant} onValueChange={(v) => setDepose({ ...depose, niveau_carburant: v })}>
              <SelectTrigger data-testid="pret-fuel"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FUEL_LEVELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <Label>Dommages préexistants / remarques</Label>
          <Textarea value={depose.etat_initial} onChange={(e) => setDepose({ ...depose, etat_initial: e.target.value })} rows={2} placeholder="Rayure aile av-droite, impact pare-brise…" data-testid="pret-etat-initial" />
        </div>
        <div className="mt-4">
          <Label className="text-xs uppercase text-gray-500 font-semibold mb-2 block">Photos des 4 coins</Label>
          <PhotosCorners photos={photos} onChange={setPhotos} />
        </div>
      </Card>

      {/* 5. Tarif / Caution */}
      <Card className="p-5 border-gray-200 mb-4" data-testid="pret-step-tarif">
        <div className="text-xs uppercase tracking-wider text-[#52a675] font-bold mb-3">5 · TARIF & CAUTION</div>
        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input type="checkbox" checked={depose.courtoisie} onChange={(e) => setDepose({ ...depose, courtoisie: e.target.checked })} className="w-4 h-4 accent-[#52a675]" data-testid="pret-courtoisie" />
          <span className="text-sm">Prêt courtoisie (gratuit, pas de facturation)</span>
        </label>
        {!depose.courtoisie && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tarif par jour (CHF)</Label>
              <Input type="number" step="1" value={depose.tarif_jour} onChange={(e) => setDepose({ ...depose, tarif_jour: e.target.value })} data-testid="pret-tarif-jour" />
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label>Caution (CHF)</Label>
            <Input type="number" step="50" value={depose.caution_montant} onChange={(e) => setDepose({ ...depose, caution_montant: e.target.value })} data-testid="pret-caution-montant" />
          </div>
          <div>
            <Label>Mode caution</Label>
            <Select value={depose.caution_mode} onValueChange={(v) => setDepose({ ...depose, caution_mode: v })}>
              <SelectTrigger data-testid="pret-caution-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aucune">Aucune</SelectItem>
                <SelectItem value="especes">Espèces</SelectItem>
                <SelectItem value="cb">Empreinte CB</SelectItem>
                <SelectItem value="virement">Virement</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* 6. Signatures — section supprimée (non utilisée pour le moment) */}

      {/* Submit fixed bottom */}
      <div className="sticky bottom-0 -mx-4 lg:-mx-8 px-4 lg:px-8 py-3 bg-white border-t border-gray-200 flex justify-end gap-2 z-10">
        <Button variant="outline" onClick={() => navigate("/remplacement")}>Annuler</Button>
        <Button onClick={submit} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="pret-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer le prêt"}
        </Button>
      </div>
    </div>
  );
}
